import { useState } from 'react';
import { api, usd } from '../lib/api';
import { ErrorNote, useLoad } from '../components/ui';

interface WebOrder {
  id: string; number: number; customer_name: string; phone: string; note: string | null; source?: string; external_ref?: string | null;
  subtotal_cents: number; status: 'new' | 'ready' | 'picked_up' | 'cancelled'; created_at: string;
  lines: { description: string; qty: number; unit_price_cents: number }[];
}

const STATUS: Record<WebOrder['status'], [string, string]> = {
  new: ['New', 'warn'], ready: ['Ready for pickup', 'good'], picked_up: ['Picked up', ''], cancelled: ['Cancelled', 'bad'],
};

export default function WebOrders() {
  const [open, setOpen] = useState(true);
  const { data, error, reload } = useLoad(() => api<WebOrder[]>(`/api/web-orders?open=${open}`), [open]);
  const [err, setErr] = useState<unknown>(null);

  async function setStatus(o: WebOrder, status: WebOrder['status']) {
    if (status === 'cancelled' && !confirm(`Cancel order #${o.number} for ${o.customer_name}?`)) return;
    setErr(null);
    try { await api(`/api/web-orders/${o.id}/status`, { body: { status } }); reload(); } catch (e) { setErr(e); }
  }

  return (
    <div className="stack" style={{ maxWidth: 980 }}>
      <div className="page-head">
        <div className="grow">
          <h1>Web orders</h1>
          <p>Pickup orders from your store website. Pull the items and mark the order ready, then call the customer. At pickup, ring the items up at the register as a normal sale: scan their ID and take payment there. Then mark the order picked up.</p>
        </div>
        <div className="row">
          <button className={open ? 'primary' : ''} onClick={() => setOpen(true)}>Open</button>
          <button className={open ? '' : 'primary'} onClick={() => setOpen(false)}>Last 30 days</button>
        </div>
      </div>
      <ErrorNote error={error ?? err} />
      {data && data.length === 0 && <div className="empty">{open ? 'No open orders.' : 'No orders in the last 30 days.'}</div>}
      {data?.map((o) => (
        <div className="panel" key={o.id}>
          <div className="panel-head">
            <h2 className="grow">#{o.number} · {o.customer_name} <span className="faint num">{o.phone}</span>{o.source === 'api' && <span className="chip" style={{ marginLeft: 8 }}>from your app{o.external_ref ? ` · ${o.external_ref}` : ''}</span>}</h2>
            <span className={`chip ${STATUS[o.status][1]}`}>{STATUS[o.status][0]}</span>
            <span className="faint">{new Date(o.created_at).toLocaleString()}</span>
          </div>
          <div className="panel-body stack-s">
            <table><tbody>
              {o.lines.map((l, i) => <tr key={i}><td className="num" style={{ whiteSpace: 'nowrap', paddingRight: 8 }}>{l.qty} ×</td><td style={{ width: '100%' }}>{l.description}</td><td className="num">{usd(l.qty * l.unit_price_cents)}</td></tr>)}
              <tr><td /><td><b>Estimated total</b> <span className="faint">(before tax)</span></td><td className="num"><b>{usd(o.subtotal_cents)}</b></td></tr>
            </tbody></table>
            {o.note && <div className="notice">Note: {o.note}</div>}
            <div className="row wrap">
              {o.status === 'new' && <button className="primary" onClick={() => setStatus(o, 'ready')}>Mark ready</button>}
              {(o.status === 'new' || o.status === 'ready') && <button onClick={() => setStatus(o, 'picked_up')}>Picked up</button>}
              {(o.status === 'new' || o.status === 'ready') && <button className="ghost" onClick={() => setStatus(o, 'cancelled')}>Cancel order</button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
